/**
 * SlotScreenImageService
 *
 * Saves generated screen images to the project folder so they persist across sessions.
 * Images are stored at: .editor/slot-screens/<screenId>/<resultId>.png
 */

import type { SlotScreenId, ScreenResult } from '../../../core/types/slot-screen-generator.types';
import { SLOT_SCREEN_ORDER } from '../../../core/types/slot-screen-generator.types';
import type { ComponentResult } from '../../../core/types/slot-screen-component.types';
import {
  hasProjectRoot,
  writeFileAsArrayBuffer,
  readFileAsArrayBuffer,
  deleteFile,
  listDirectory,
} from '../FileSystemService';

const SLOT_SCREENS_DIR = '.editor/slot-screens';

function resultPath(screenId: SlotScreenId, resultId: string): string {
  return `${SLOT_SCREENS_DIR}/${screenId}/${resultId}.png`;
}

/**
 * Fetch a blob/object URL as ArrayBuffer.
 */
async function fetchBlobAsBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch blob: ${res.status}`);
  return res.arrayBuffer();
}

/**
 * Save a screen result image to the project filesystem.
 * Returns the saved relative path, or null if no project root.
 */
export async function saveScreenResultImage(
  screenId: SlotScreenId,
  result: ScreenResult,
): Promise<string | null> {
  if (!hasProjectRoot() || !result.imageUrl) return null;

  const path = resultPath(screenId, result.id);
  const buffer = await fetchBlobAsBuffer(result.imageUrl);
  await writeFileAsArrayBuffer(path, buffer);
  return path;
}

/**
 * Load a saved image from disk and return a blob URL.
 * Returns null if file not found or no project root.
 */
export async function loadScreenResultImage(savedPath: string): Promise<string | null> {
  if (!hasProjectRoot()) return null;

  try {
    const buffer = await readFileAsArrayBuffer(savedPath);
    const blob = new Blob([buffer], { type: 'image/png' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Restore all saved screen images from disk.
 * Returns a map: screenId → array of { resultId, blobUrl, savedPath }.
 */
export async function restoreAllScreenImages(): Promise<
  Record<string, Array<{ resultId: string; blobUrl: string; savedPath: string }>>
> {
  const result: Record<string, Array<{ resultId: string; blobUrl: string; savedPath: string }>> = {};
  if (!hasProjectRoot()) return result;

  for (const screenId of SLOT_SCREEN_ORDER) {
    const dirPath = `${SLOT_SCREENS_DIR}/${screenId}`;
    try {
      const entries = await listDirectory(dirPath);
      const images: Array<{ resultId: string; blobUrl: string; savedPath: string }> = [];

      for (const entry of entries) {
        if (entry.kind === 'file' && entry.name.endsWith('.png')) {
          const resultId = entry.name.replace('.png', '');
          const filePath = `${dirPath}/${entry.name}`;
          try {
            const buffer = await readFileAsArrayBuffer(filePath);
            const blob = new Blob([buffer], { type: 'image/png' });
            images.push({
              resultId,
              blobUrl: URL.createObjectURL(blob),
              savedPath: filePath,
            });
          } catch {
            // Skip unreadable files
          }
        }
      }

      if (images.length > 0) {
        result[screenId] = images;
      }
    } catch {
      // Directory doesn't exist yet — skip
    }
  }

  return result;
}

/**
 * Delete a saved screen result image from the filesystem.
 */
export async function deleteScreenResultImage(savedPath: string): Promise<void> {
  if (!hasProjectRoot() || !savedPath) return;
  try {
    await deleteFile(savedPath);
  } catch {
    // File may already be gone
  }
}

// ─── Video Persistence ──────────────────────────────────

const SLOT_VIDEOS_DIR = '.editor/slot-videos';

/**
 * Save a generated video blob to the project filesystem.
 * Path: .editor/slot-videos/<entryId>.webm (or .mp4)
 * Returns the saved relative path, or null if no project root.
 */
export async function saveVideoToProject(
  entryId: string,
  videoBlob: Blob,
): Promise<string | null> {
  if (!hasProjectRoot()) return null;
  const ext = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
  const path = `${SLOT_VIDEOS_DIR}/${entryId}.${ext}`;
  const buffer = await videoBlob.arrayBuffer();
  await writeFileAsArrayBuffer(path, buffer);
  return path;
}

/**
 * Load a saved video from disk and return a blob URL.
 * Returns null if file not found or no project root.
 */
export async function loadVideoFromProject(savedPath: string): Promise<string | null> {
  if (!hasProjectRoot() || !savedPath) return null;
  try {
    const buffer = await readFileAsArrayBuffer(savedPath);
    const ext = savedPath.endsWith('.mp4') ? 'video/mp4' : 'video/webm';
    const blob = new Blob([buffer], { type: ext });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Delete a saved video from the filesystem.
 */
export async function deleteVideoFromProject(savedPath: string): Promise<void> {
  if (!hasProjectRoot() || !savedPath) return;
  try {
    await deleteFile(savedPath);
  } catch {
    // File may already be gone
  }
}

// ─── Idea Canvas Persistence ──────────────────────────────

const IDEA_CANVAS_DIR = '.editor/idea-canvas';

/**
 * Save a chat message's images to disk.
 * Path: .editor/idea-canvas/<sessionId>/<msgId>_<index>.png
 */
export async function saveIdeaMessageImages(
  sessionId: string,
  msgId: string,
  imageUrls: string[],
): Promise<string[]> {
  if (!hasProjectRoot() || imageUrls.length === 0) return [];

  const paths: string[] = [];
  for (let i = 0; i < imageUrls.length; i++) {
    const url = imageUrls[i];
    if (!url) continue;
    try {
      const path = `${IDEA_CANVAS_DIR}/${sessionId}/${msgId}_${i}.png`;
      const buffer = await fetchBlobAsBuffer(url);
      await writeFileAsArrayBuffer(path, buffer);
      paths.push(path);
    } catch {
      // Skip failed saves
    }
  }
  return paths;
}

/**
 * Load all images for a message from disk. Returns blob URLs.
 */
export async function loadIdeaMessageImages(savedPaths: string[]): Promise<string[]> {
  if (!hasProjectRoot()) return [];

  const urls: string[] = [];
  for (const path of savedPaths) {
    try {
      const buffer = await readFileAsArrayBuffer(path);
      const blob = new Blob([buffer], { type: 'image/png' });
      urls.push(URL.createObjectURL(blob));
    } catch {
      // Skip unreadable files
    }
  }
  return urls;
}

/**
 * Delete all saved files for an idea canvas session.
 */
export async function deleteIdeaSessionFiles(sessionId: string): Promise<void> {
  if (!hasProjectRoot() || !sessionId) return;
  const dirPath = `${IDEA_CANVAS_DIR}/${sessionId}`;
  try {
    const entries = await listDirectory(dirPath);
    for (const entry of entries) {
      if (entry.kind === 'file') {
        await deleteFile(`${dirPath}/${entry.name}`).catch(() => {});
      }
    }
  } catch {
    // Directory doesn't exist
  }
}

// ─── Screen Builder Component Persistence ────────────────

const SCREEN_BUILDER_DIR = '.editor/screen-builder';

function componentResultPath(
  screenId: string,
  componentId: string,
  resultId: string,
  type: 'image' | 'video',
): string {
  const ext = type === 'video' ? 'mp4' : 'png';
  return `${SCREEN_BUILDER_DIR}/${screenId}/${componentId}/${resultId}.${ext}`;
}

/**
 * Save a component result (image or video) to the project filesystem.
 * Returns the saved relative path, or null if no project root.
 */
export async function saveComponentImage(
  screenId: string,
  componentId: string,
  result: ComponentResult,
): Promise<string | null> {
  if (!hasProjectRoot()) return null;
  const url = result.type === 'video' ? result.videoUrl : result.imageUrl;
  if (!url) return null;
  const path = componentResultPath(screenId, componentId, result.id, result.type);
  const buffer = await fetchBlobAsBuffer(url);
  await writeFileAsArrayBuffer(path, buffer);
  return path;
}

/**
 * Load a saved component image/video from disk and return a blob URL.
 * Returns null if file not found or no project root.
 */
export async function loadComponentImage(savedPath: string): Promise<string | null> {
  if (!hasProjectRoot()) return null;
  try {
    const buffer = await readFileAsArrayBuffer(savedPath);
    const isVideo = savedPath.endsWith('.mp4') || savedPath.endsWith('.webm');
    const blob = new Blob([buffer], { type: isVideo ? 'video/mp4' : 'image/png' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Delete a saved component result from the filesystem.
 */
export async function deleteComponentImage(savedPath: string): Promise<void> {
  if (!hasProjectRoot()) return;
  try {
    await deleteFile(savedPath);
  } catch {
    // File may already be gone
  }
}
